import React, { useMemo } from 'react';
import {
  TrendingDown,
  TrendingUp,
  Minus,
  Clock,
  BarChart2,
  Target,
  AlertTriangle,
  BarChart3,
  DollarSign,
  Timer,
  Activity,
  Building2,
  Crosshair,
  Shield,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  TrendingDown as TrendingDownIcon
} from 'lucide-react';
import { CalculatedMetrics, KPIMetrics } from '../types';
import { formatPercentage, formatVolume, formatMarketCap } from '../utils/calculations';
import { aggregateByPriceRange, aggregateByMarketCap } from '../utils/analysisCalculations';

interface GapInsightsProps {
  calculatedData: CalculatedMetrics[];
  kpis: KPIMetrics;
}

// Estendi l'interfaccia per includere azioni specifiche
interface EnhancedInsight {
  icon: React.ComponentType<{ className?: string }>;
  text: string;
  type: 'success' | 'warning' | 'info' | 'danger';
  action: string;
}
export const GapInsights: React.FC<GapInsightsProps> = ({ calculatedData, kpis }) => {
  const insights = useMemo((): EnhancedInsight[] => {
    if (calculatedData.length === 0) {
      return [
        {
          icon: AlertTriangle,
          text: "Nessun dato disponibile per generare insight. Carica un database per visualizzare analisi dettagliate.",
          type: 'warning' as const,
          action: "Carica un database dalla sezione principale per iniziare l'analisi"
        }
      ];
    }

    // Determina il bias complessivo in base alla percentuale di chiusure rosse
    const overallBias = kpis.closeRedPercentage < 45 
      ? 'LONG' 
      : kpis.closeRedPercentage > 55 
        ? 'SHORT' 
        : 'NEUTRALE';

    const insights = [];

    // 1. Insight su tasso di chiusure rosse (dinamico in base al bias)
    if (overallBias === 'LONG') {
      insights.push({
        icon: TrendingUp,
        text: `BIAS LONG CONFERMATO: Solo ${kpis.closeRedPercentage.toFixed(1)}% dei gap chiude in rosso (vs 50% neutrale)`,
        type: 'success' as const,
        action: `Azione: Cerca attivamente setup long. Considera position sizing del 2-3% del capitale per setup con alta qualità. Target: chiusure verdi nel ${(100 - kpis.closeRedPercentage).toFixed(1)}% dei casi.`
      });
    } else if (overallBias === 'NEUTRALE') {
      insights.push({
        icon: Minus,
        text: `MERCATO NEUTRALE: ${kpis.closeRedPercentage.toFixed(1)}% chiusure rosse (range 45-55% = neutrale)`,
        type: 'info' as const,
        action: `Azione: Selezione rigorosa dei setup. Usa filtri aggiuntivi (volume, market cap, timing) per identificare opportunità. Position sizing conservativo 1-1.5% del capitale.`
      });
    } else { // SHORT
      insights.push({
        icon: TrendingDown,
        text: `BIAS SHORT CONFERMATO: ${kpis.closeRedPercentage.toFixed(1)}% dei gap chiude in rosso (>55% = bias short)`,
        type: 'success' as const,
        action: `Azione: Focus su strategie short o evita posizioni long. Per short: entry su spike/rimbalzi, target verso LOD medio (${kpis.lodAverageTime}). Position sizing 1.5-2.5% del capitale.`
      });
    }

    // 2. Insight su timing HOD/LOD (adattato al bias)
    const hodHour = parseInt(kpis.hodAverageTime.split(':')[0]);
    const lodHour = parseInt(kpis.lodAverageTime.split(':')[0]);
    
    if (overallBias === 'LONG') {
      if (hodHour >= 13) {
        insights.push({
          icon: Clock,
          text: `TIMING LONG FAVOREVOLE: HOD medio alle ${kpis.hodAverageTime} (pomeridiano) - momentum sostenuto`,
          type: 'success' as const,
          action: `Azione: Entry mattutino (9:30-10:30) con holding fino alle ${kpis.hodAverageTime}. Considera scaling out: 30% a +15%, 40% a +25%, 30% runner fino a HOD. Stop loss sotto apertura.`
        });
      } else {
        insights.push({
          icon: Clock,
          text: `TIMING LONG RAPIDO: HOD medio alle ${kpis.hodAverageTime} (mattutino) - movimento concentrato`,
          type: 'info' as const,
          action: `Azione: Entry immediato all'apertura, profit taking aggressivo: 50% entro le 10:30, 30% entro le 11:30, 20% runner. Monitora volume primi 15 minuti per conferma.`
        });
      }
    } else if (overallBias === 'SHORT') {
      if (hodHour <= 11 && lodHour >= 13) {
        insights.push({
          icon: Clock,
          text: `TIMING SHORT PERFETTO: HOD alle ${kpis.hodAverageTime} (mattutino), LOD alle ${kpis.lodAverageTime} (pomeridiano)`,
          type: 'success' as const,
          action: `Azione: Attendi spike mattutino fino alle ${kpis.hodAverageTime}, poi entry short. Target principale verso ${kpis.lodAverageTime}. Stop loss 3-5% sopra HOD confermato.`
        });
      } else {
        insights.push({
          icon: Clock,
          text: `TIMING SHORT VARIABILE: HOD alle ${kpis.hodAverageTime}, LOD alle ${kpis.lodAverageTime}`,
          type: 'info' as const,
          action: `Azione: Monitora formazione HOD intraday. Entry short su conferma di debolezza (volume in calo, break supporti). Target flessibili basati su livelli tecnici.`
        });
      }
    } else { // NEUTRALE
      insights.push({
        icon: Clock,
        text: `PATTERN TEMPORALE: HOD alle ${kpis.hodAverageTime}, LOD alle ${kpis.lodAverageTime}`,
        type: 'info' as const,
        action: `Azione: Strategia bidirezionale. Long su pullback verso LOD, short su test HOD falliti. Usa livelli tecnici e volume per conferme. Position sizing ridotto (1% capitale).`
      });
    }

    // 3. Insight su volume e liquidità (adattato al bias)
    if (overallBias === 'LONG') {
      if (kpis.pmEodVolumeRatio > 5) {
        insights.push({
          icon: BarChart2,
          text: `LIQUIDITÀ ALTA: Volume EOD ${kpis.pmEodVolumeRatio.toFixed(1)}x il PM (>5x = eccellente)`,
          type: 'success' as const,
          action: `Azione: Scaling out progressivo facilitato. Piano: 25% a T1 (+10-15%), 35% a T2 (+20-25%), 25% a T3 (+30-40%), 15% runner. Monitora volume real-time per timing exit.`
        });
      } else {
        insights.push({
          icon: BarChart2,
          text: `LIQUIDITÀ LIMITATA: Volume EOD ${kpis.pmEodVolumeRatio.toFixed(1)}x il PM (<5x = attenzione)`,
          type: 'warning' as const,
          action: `Azione: Position sizing ridotto (1-1.5% capitale). Exit concentrato: 60% al primo target (+8-12%), 40% al secondo (+15-20%). Evita holding prolungato.`
        });
      }
    } else if (overallBias === 'SHORT') {
      if (kpis.pmEodVolumeRatio > 5) {
        insights.push({
          icon: BarChart2,
          text: `LIQUIDITÀ ALTA PER SHORT: Volume EOD ${kpis.pmEodVolumeRatio.toFixed(1)}x il PM`,
          type: 'success' as const,
          action: `Azione: Short con exit rapidi facilitati. Entry su spike/HOD, target: 40% a -8%, 35% a -15%, 25% a -25%. Stop loss stretto 2-3% sopra entry.`
        });
      } else {
        insights.push({
          icon: BarChart2,
          text: `LIQUIDITÀ LIMITATA PER SHORT: Volume EOD ${kpis.pmEodVolumeRatio.toFixed(1)}x il PM`,
          type: 'warning' as const,
          action: `Azione: Short con sizing ridotto (0.5-1% capitale). Exit rapido al primo target (-5 a -10%). Evita short su titoli illiquidi.`
        });
      }
    } else { // NEUTRALE
      insights.push({
        icon: BarChart2,
        text: `VOLUME RATIO: EOD ${kpis.pmEodVolumeRatio.toFixed(1)}x PM`,
        type: 'info' as const,
        action: `Azione: Adatta sizing al volume ratio. >5x = sizing normale, 3-5x = sizing ridotto, <3x = sizing minimo. Monitora volume primi 30 min per conferme direzionali.`
      });
    }

    // 4. Insight su PMH break rate (adattato al bias)
    if (overallBias === 'LONG') {
      if (kpis.pmhBreakPercentage > 60) {
        insights.push({
          icon: Target,
          text: `BREAKOUT PATTERN: ${kpis.pmhBreakPercentage.toFixed(1)}% supera PMH (>60% = forte)`,
          type: 'success' as const,
          action: `Azione: Entry su pullback verso PMH o breakout confermato. Livelli: Entry a PMH-2%, Target 1 a PMH+15%, Target 2 a PMH+30%. Stop loss sotto PMH-5%.`
        });
      } else {
        insights.push({
          icon: Target,
          text: `PMH COME RESISTENZA: Solo ${kpis.pmhBreakPercentage.toFixed(1)}% supera PMH (<60% = resistenza)`,
          type: 'warning' as const,
          action: `Azione: PMH è livello di presa profitti chiave. Piano: 50% dei profitti a PMH-3%, 30% a PMH, 20% runner solo su breakout con volume. Stop loss sotto apertura.`
        });
      }
    } else if (overallBias === 'SHORT') {
      if (kpis.pmhBreakPercentage < 30) {
        insights.push({
          icon: Target,
          text: `PMH RESISTENZA FORTE: Solo ${kpis.pmhBreakPercentage.toFixed(1)}% supera PMH (<30% = resistenza forte)`,
          type: 'success' as const,
          action: `Azione: Short dal PMH con alta probabilità. Entry: PMH-1% a PMH+2%, Target: -15% a -25% dal PMH. Stop loss: PMH+5%. Attendi test del PMH per entry.`
        });
      } else {
        insights.push({
          icon: Target,
          text: `BREAKOUT FREQUENTI: ${kpis.pmhBreakPercentage.toFixed(1)}% supera PMH (>30% = breakout comuni)`,
          type: 'warning' as const,
          action: `Azione: Attendi fallimento breakout per short. Segnali: volume in calo su test PMH, rejection candle, break sotto PMH dopo test fallito. Entry solo con conferme.`
        });
      }
    } else { // NEUTRALE
      insights.push({
        icon: Target,
        text: `PMH LIVELLO CHIAVE: ${kpis.pmhBreakPercentage.toFixed(1)}% supera PMH`,
        type: 'info' as const,
        action: `Azione: PMH come pivot point. Long su pullback verso PMH (entry PMH-3%), short su rejection da PMH (entry PMH+2%). Gestione bidirezionale con stop stretti.`
      });
    }

    // 5. Insight su Open to PMH (NUOVO)
    const openToPmhPercent = kpis.averageOpenToPmh * 100;
    if (overallBias === 'LONG') {
      if (openToPmhPercent < -5) {
        insights.push({
          icon: BarChart3,
          text: `AMPIO SPAZIO RIALZISTA: Apertura ${formatPercentage(kpis.averageOpenToPmh)} sotto PMH (>-5% = spazio ampio)`,
          type: 'success' as const,
          action: `Azione: Entry all'apertura con target PMH. Piano: Entry a Open, Target 1 a PMH-2% (${Math.abs(openToPmhPercent-2).toFixed(1)}% gain), Target 2 a PMH+5%. R/R favorevole.`
        });
      } else {
        insights.push({
          icon: BarChart3,
          text: `APERTURA VICINA PMH: Gap ${formatPercentage(kpis.averageOpenToPmh)} al PMH (<-5% = spazio limitato)`,
          type: 'warning' as const,
          action: `Azione: Attendi pullback per entry migliore. Target pullback: Open-3% a Open-8%. Entry su rimbalzo con target PMH. Evita chase all'apertura.`
        });
      }
    } else if (overallBias === 'SHORT') {
      if (openToPmhPercent > -2) {
        insights.push({
          icon: BarChart3,
          text: `SETUP SHORT IDEALE: Apertura ${formatPercentage(kpis.averageOpenToPmh)} dal PMH (>-2% = vicina)`,
          type: 'success' as const,
          action: `Azione: Short dall'apertura o su test PMH. Entry: Open a PMH+1%, Stop: PMH+3%, Target 1: Open-10%, Target 2: Open-20%. R/R 3:1 o superiore.`
        });
      } else {
        insights.push({
          icon: BarChart3,
          text: `APERTURA DISTANTE DA PMH: ${formatPercentage(kpis.averageOpenToPmh)} sotto PMH`,
          type: 'info' as const,
          action: `Azione: Attendi movimento verso PMH per short. Segnali: spike verso PMH con volume, test e rejection. Entry su conferma debolezza, non su anticipazione.`
        });
      }
    } else { // NEUTRALE
      insights.push({
        icon: BarChart3,
        text: `DISTANZA PMH: ${formatPercentage(kpis.averageOpenToPmh)} dall'apertura al PMH`,
        type: 'info' as const,
        action: `Azione: Usa PMH come livello pivot. Range trading: long su pullback da PMH, short su rejection da PMH. Gestione neutrale con target simmetrici.`
      });
    }

    // 6. Insight su Performance Primi 30 Minuti (NUOVO)
    if (overallBias === 'LONG') {
      if (kpis.average30MinHighPercent > 0.05) { // 5%
        insights.push({
          icon: Activity,
          text: `MOMENTUM 30MIN FORTE: Spike medio ${formatPercentage(kpis.average30MinHighPercent)} (>5% = forte)`,
          type: 'success' as const,
          action: `Azione: Entry aggressivo primi 5-10 minuti. Se spike >3% in 10min = conferma momentum. Target rapido: 50% posizione a +${(kpis.average30MinHighPercent * 100 * 0.8).toFixed(1)}% entro 30min.`
        });
      } else {
        insights.push({
          icon: Activity,
          text: `MOMENTUM 30MIN MODERATO: Spike medio ${formatPercentage(kpis.average30MinHighPercent)} (<5% = moderato)`,
          type: 'info' as const,
          action: `Azione: Approccio cauto. Attendi conferma trend dopo 30min. Entry su pullback, target conservativi: +8-12% per primo target. Monitora volume per conferme.`
        });
      }
    } else if (overallBias === 'SHORT') {
      if (kpis.average30MinLowPercent < -0.05) { // -5%
        insights.push({
          icon: Activity,
          text: `PULLBACK 30MIN RAPIDO: Drawdown ${formatPercentage(kpis.average30MinLowPercent)} (<-5% = rapido)`,
          type: 'success' as const,
          action: `Azione: Short aggressivo su spike iniziale. Entry su primo rimbalzo dopo spike, target: 50% a ${(Math.abs(kpis.average30MinLowPercent) * 100 * 0.7).toFixed(1)}% entro 30min.`
        });
      } else {
        insights.push({
          icon: Activity,
          text: `PULLBACK 30MIN LENTO: Drawdown ${formatPercentage(kpis.average30MinLowPercent)} (>-5% = lento)`,
          type: 'info' as const,
          action: `Azione: Short più selettivo. Attendi segnali di debolezza: break supporti, volume in calo, pattern reversal. Entry con conferme multiple.`
        });
      }
    } else { // NEUTRALE
      insights.push({
        icon: Activity,
        text: `RANGE 30MIN: +${formatPercentage(kpis.average30MinHighPercent)} / ${formatPercentage(kpis.average30MinLowPercent)}`,
        type: 'info' as const,
        action: `Azione: Range trading primi 30min. Long su test low del range, short su test high. Target: metà del range opposto. Stop: fuori dal range.`
      });
    }

    // 7. Insight su fasce di prezzo più favorevoli (adattato al bias)
    const priceRanges = aggregateByPriceRange(calculatedData);
    
    if (overallBias === 'LONG') {
      const bestPriceRange = priceRanges.reduce((best, current) => 
        current.count > 10 && current.successRate > best.successRate ? current : best
      , priceRanges[0]);
      
      insights.push({
        icon: DollarSign,
        text: `FASCIA PREZZO OTTIMALE LONG: ${bestPriceRange.label} con ${bestPriceRange.successRate.toFixed(1)}% successo`,
        type: 'success' as const,
        action: `Azione: Priorità assoluta a titoli in fascia ${bestPriceRange.label}. Spike medio atteso: ${bestPriceRange.avgSpike.toFixed(1)}%. Volume ratio medio: ${bestPriceRange.avgVolumeRatio.toFixed(1)}x. Sizing aumentato per questa fascia.`
      });
    } else if (overallBias === 'SHORT') {
      const worstPriceRange = priceRanges.reduce((worst, current) => 
        current.count > 10 && current.redCloseRate > worst.redCloseRate ? current : worst
      , priceRanges[0]);
      
      insights.push({
        icon: DollarSign,
        text: `FASCIA PREZZO OTTIMALE SHORT: ${worstPriceRange.label} con ${worstPriceRange.redCloseRate.toFixed(1)}% chiusure rosse`,
        type: 'success' as const,
        action: `Azione: Focus su titoli fascia ${worstPriceRange.label}. Spike medio: ${worstPriceRange.avgSpike.toFixed(1)}% (attendi per entry). Volume ratio: ${worstPriceRange.avgVolumeRatio.toFixed(1)}x. Sizing standard per questa fascia.`
      });
    }

    // 8. Insight su market cap e bias
    const marketCapRanges = aggregateByMarketCap(calculatedData);
    
    if (overallBias === 'LONG') {
      const bestMarketCap = marketCapRanges.reduce((best, current) => 
        current.count > 5 && current.successRate > best.successRate ? current : best
      , marketCapRanges[0]);
      
      insights.push({
        icon: Building2,
        text: `MARKET CAP PREFERENZIALE: ${bestMarketCap.label} - ${bestMarketCap.successRate.toFixed(1)}% successo`,
        type: 'success' as const,
        action: `Azione: Screening prioritario su ${bestMarketCap.label}. PMH break rate: ${bestMarketCap.pmhBreakRate.toFixed(1)}%. Spike medio: ${bestMarketCap.avgSpike.toFixed(1)}%. Aumenta allocation su questa categoria.`
      });
    } else if (overallBias === 'SHORT') {
      const worstMarketCap = marketCapRanges.reduce((worst, current) => 
        current.count > 5 && current.redCloseRate > worst.redCloseRate ? current : worst
      , marketCapRanges[0]);
      
      insights.push({
        icon: Building2,
        text: `MARKET CAP TARGET SHORT: ${worstMarketCap.label} - ${worstMarketCap.redCloseRate.toFixed(1)}% chiusure rosse`,
        type: 'success' as const,
        action: `Azione: Focus short su ${worstMarketCap.label}. Spike medio: ${worstMarketCap.avgSpike.toFixed(1)}% (livello entry). Volume ratio: ${worstMarketCap.avgVolumeRatio.toFixed(1)}x. Priorità alta per questa categoria.`
      });
    }

    // 9. Insight su spike medio e mean reversion (adattato al bias)
    if (overallBias === 'LONG') {
      if (kpis.averageSpikePercent > 0.15) { // 15%
        insights.push({
          icon: TrendingUp,
          text: `SPIKE ELEVATO: Media ${formatPercentage(kpis.averageSpikePercent)} (>15% = alto potenziale)`,
          type: 'success' as const,
          action: `Azione: Target estesi giustificati. Piano: T1 a +${(kpis.averageSpikePercent * 100 * 0.4).toFixed(1)}%, T2 a +${(kpis.averageSpikePercent * 100 * 0.7).toFixed(1)}%, T3 a +${(kpis.averageSpikePercent * 100).toFixed(1)}%. Stop loss -8% da entry.`
        });
      }
    } else if (overallBias === 'SHORT') {
      if (kpis.averageSpikePercent > 0.15) { // 15%
        insights.push({
          icon: TrendingDown,
          text: `MEAN REVERSION PATTERN: Spike medio ${formatPercentage(kpis.averageSpikePercent)} seguito da correzione`,
          type: 'success' as const,
          action: `Azione: Short dopo spike >+${(kpis.averageSpikePercent * 100 * 0.8).toFixed(1)}%. Entry su primo pullback da HOD. Target: -${(kpis.averageSpikePercent * 100 * 0.6).toFixed(1)}% a -${(kpis.averageSpikePercent * 100 * 0.9).toFixed(1)}%. Stop: HOD+3%.`
        });
      }
    }

    // Limita a 7 insight per non sovraccaricare l'UI
    return insights.slice(0, 7);
  }, [calculatedData, kpis]);

  // Determina il bias complessivo per lo styling del pannello
  const overallBias = useMemo(() => {
    if (calculatedData.length === 0) return 'NEUTRALE';
    return kpis.closeRedPercentage < 45 
      ? 'LONG' 
      : kpis.closeRedPercentage > 55 
        ? 'SHORT' 
        : 'NEUTRALE';
  }, [calculatedData, kpis]);

  // Configura lo stile del pannello in base al bias
  const getPanelStyle = () => {
    switch (overallBias) {
      case 'LONG':
        return {
          headerBg: 'bg-green-400/10',
          headerText: 'text-green-400',
          headerIcon: <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />,
          title: 'INSIGHTS GAP - LONG BIAS'
        };
      case 'SHORT':
        return {
          headerBg: 'bg-red-400/10',
          headerText: 'text-red-400',
          headerIcon: <TrendingDown className="h-4 w-4 sm:h-5 sm:w-5 text-red-400" />,
          title: 'INSIGHTS GAP - SHORT BIAS'
        };
      default:
        return {
          headerBg: 'bg-blue-400/10',
          headerText: 'text-blue-400',
          headerIcon: <Minus className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400" />,
          title: 'INSIGHTS GAP - BIAS NEUTRALE'
        };
    }
  };

  const panelStyle = getPanelStyle();

  return (
    <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
      <div className="flex items-center space-x-2 sm:space-x-3 mb-4 sm:mb-6">
        <div className={`w-2 h-2 ${overallBias === 'LONG' ? 'bg-green-400' : overallBias === 'SHORT' ? 'bg-red-400' : 'bg-blue-400'} rounded-full animate-pulse`}></div>
        <h3 className="text-base sm:text-lg font-semibold text-white">{panelStyle.title}</h3>
        <div className={`px-2 py-1 ${panelStyle.headerBg} ${panelStyle.headerText} text-xs rounded-full border ${overallBias === 'LONG' ? 'border-green-400/30' : overallBias === 'SHORT' ? 'border-red-400/30' : 'border-blue-400/30'}`}>
          {overallBias === 'NEUTRALE' ? 'NEUTRALE' : `${overallBias}`}
        </div>
      </div>
      
      <div className="space-y-4 sm:space-y-6">
        {insights.map((insight, index) => {
          const bgColor = insight.type === 'success' 
            ? `${overallBias === 'LONG' ? 'bg-green-900/30 hover:bg-green-900/50 border-green-400/20' : 'bg-red-900/30 hover:bg-red-900/50 border-red-400/20'}`
            : insight.type === 'warning' 
            ? 'bg-yellow-900/30 hover:bg-yellow-900/50 border-yellow-400/20'
            : insight.type === 'danger'
            ? 'bg-red-900/30 hover:bg-red-900/50 border-red-400/20'
            : 'bg-blue-900/30 hover:bg-blue-900/50 border-blue-400/20';
          
          const iconColor = insight.type === 'success' 
            ? `${overallBias === 'LONG' ? 'text-green-400 bg-green-400/10' : 'text-red-400 bg-red-400/10'}`
            : insight.type === 'warning' 
            ? 'text-yellow-400 bg-yellow-400/10'
            : insight.type === 'danger'
            ? 'text-red-400 bg-red-400/10'
            : 'text-blue-400 bg-blue-400/10';

          return (
            <div key={index} className={`p-4 sm:p-6 rounded-lg border transition-all duration-200 ${bgColor}`}>
              {/* Header dell'insight */}
              <div className="flex items-start space-x-3 sm:space-x-4 mb-3">
                <div className={`p-1.5 sm:p-2 rounded-lg ${iconColor} flex-shrink-0`}>
                  <insight.icon className="h-4 w-4 sm:h-5 sm:w-5" />
                </div>
                <div className="flex-1">
                  <h4 className="text-sm sm:text-base font-semibold text-white mb-2">
                    {insight.text}
                  </h4>
                </div>
              </div>
              
              {/* Sezione Azione */}
              <div className="ml-12 sm:ml-16">
                <div className="flex items-start space-x-2 p-3 bg-gray-900/40 rounded-lg border border-gray-600/50">
                  <div className="p-1 bg-blue-400/10 rounded">
                    <Crosshair className="h-3 w-3 text-blue-400" />
                  </div>
                  <div>
                    <h5 className="text-xs font-medium text-blue-400 mb-1">PIANO OPERATIVO</h5>
                    <p className="text-xs sm:text-sm text-gray-300 leading-relaxed">
                      {insight.action}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Sezione Riepilogo Operativo */}
      {calculatedData.length > 0 && (
        <div className="mt-6 pt-6 border-t border-gray-600">
          <div className="bg-gray-900/40 rounded-lg p-4 border border-gray-600">
            <div className="flex items-center space-x-3 mb-4">
              <div className={`p-2 rounded-lg ${overallBias === 'LONG' ? 'bg-green-400/10' : overallBias === 'SHORT' ? 'bg-red-400/10' : 'bg-blue-400/10'}`}>
                <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              </div>
              <div>
                <h4 className="text-sm sm:text-base font-semibold text-white">Riepilogo Strategico</h4>
                <p className="text-xs text-gray-400">Indicazioni operative basate sui {calculatedData.length} gap analizzati</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Bias Principale */}
              <div className={`p-3 rounded-lg border ${
                overallBias === 'LONG' ? 'bg-green-900/20 border-green-400/30' : 
                overallBias === 'SHORT' ? 'bg-red-900/20 border-red-400/30' : 
                'bg-blue-900/20 border-blue-400/30'
              }`}>
                <div className="flex items-center space-x-2 mb-2">
                  {overallBias === 'LONG' ? (
                    <ArrowUpRight className="h-4 w-4 text-green-400" />
                  ) : overallBias === 'SHORT' ? (
                    <ArrowDownRight className="h-4 w-4 text-red-400" />
                  ) : (
                    <Target className="h-4 w-4 text-blue-400" />
                  )}
                  <h5 className="text-sm font-medium text-white">Bias Principale</h5>
                </div>
                <p className={`text-lg font-bold ${
                  overallBias === 'LONG' ? 'text-green-400' : 
                  overallBias === 'SHORT' ? 'text-red-400' : 'text-blue-400'
                }`}>
                  {overallBias}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {kpis.closeRedPercentage.toFixed(1)}% chiusure rosse
                </p>
              </div>
              
              {/* Timing Ottimale */}
              <div className="p-3 bg-yellow-900/20 border border-yellow-400/30 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <Clock className="h-4 w-4 text-yellow-400" />
                  <h5 className="text-sm font-medium text-white">Timing Chiave</h5>
                </div>
                <p className="text-sm text-white">
                  HOD: <span className="font-bold text-yellow-400">{kpis.hodAverageTime}</span>
                </p>
                <p className="text-sm text-white">
                  LOD: <span className="font-bold text-yellow-400">{kpis.lodAverageTime}</span>
                </p>
              </div>
              
              {/* Volume Pattern */}
              <div className="p-3 bg-purple-900/20 border border-purple-400/30 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <BarChart2 className="h-4 w-4 text-purple-400" />
                  <h5 className="text-sm font-medium text-white">Volume Pattern</h5>
                </div>
                <p className="text-sm text-white">
                  Ratio: <span className="font-bold text-purple-400">{kpis.pmEodVolumeRatio.toFixed(1)}x</span>
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {kpis.pmEodVolumeRatio > 5 ? 'Liquidità alta' : 'Liquidità limitata'}
                </p>
              </div>
            </div>
            
            {/* Call to Action Finale */}
            <div className="mt-4 p-3 bg-blue-400/10 border border-blue-400/20 rounded-lg">
              <div className="flex items-start space-x-2">
                <Zap className="h-4 w-4 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h5 className="text-sm font-medium text-blue-400">Prossimi Passi Raccomandati</h5>
                  <p className="text-xs text-blue-300 mt-1">
                    {overallBias === 'LONG' 
                      ? `1. Filtra per fascia prezzo ottimale • 2. Verifica volume PM >1M • 3. Entry su pullback verso supporti • 4. Target progressivi verso HOD medio (${kpis.hodAverageTime})`
                      : overallBias === 'SHORT'
                      ? `1. Attendi spike mattutino • 2. Entry su test HOD fallito • 3. Target verso LOD medio (${kpis.lodAverageTime}) • 4. Stop loss stretto sopra HOD`
                      : `1. Identifica direzione prevalente • 2. Usa PMH come pivot • 3. Range trading con target simmetrici • 4. Position sizing conservativo`
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {calculatedData.length > 0 && (
        <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-gray-600">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>Analisi basata su {calculatedData.length} gap nel dataset corrente</span>
            <span className="hidden sm:inline">Aggiornamento automatico in tempo reale</span>
          </div>
        </div>
      )}
    </div>
  );
};